import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Wallet,
  ArrowLeft,
  Plus,
  Trash2,
  Home,
  Car,
  Utensils,
  Zap,
  Heart,
  MoreHorizontal,
  PieChart,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BudgetPlanner = ({ user }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    category: "housing",
    name: "",
    amount: "",
    currency: "EUR",
    frequency: "monthly",
    country: user?.target_country || "ireland",
  });

  const categories = [
    { value: "housing", label: "Housing", icon: Home, color: "#4A6C6F" },
    { value: "transport", label: "Transport", icon: Car, color: "#D97757" },
    { value: "food", label: "Food & Groceries", icon: Utensils, color: "#48BB78" },
    { value: "utilities", label: "Utilities", icon: Zap, color: "#ED8936" },
    { value: "healthcare", label: "Healthcare", icon: Heart, color: "#E53E3E" },
    { value: "other", label: "Other", icon: MoreHorizontal, color: "#718096" },
  ];

  useEffect(() => {
    fetchBudget();
  }, []);

  const fetchBudget = async () => {
    try {
      const response = await fetch(`${API}/budget`, { credentials: "include" });
      if (response.ok) {
        setItems(await response.json());
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name || !newItem.amount) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await fetch(`${API}/budget`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newItem,
          amount: parseFloat(newItem.amount),
        }),
      });

      if (response.ok) {
        toast.success("Budget item added!");
        setDialogOpen(false);
        setNewItem({
          category: "housing",
          name: "",
          amount: "",
          currency: "EUR",
          frequency: "monthly",
          country: user?.target_country || "ireland",
        });
        fetchBudget();
      } else {
        toast.error("Failed to add item");
      }
    } catch (error) {
      toast.error("Error adding item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const response = await fetch(`${API}/budget/${itemId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Item deleted");
        fetchBudget();
      }
    } catch (error) {
      toast.error("Error deleting item");
    }
  };

  const calculateMonthlyTotal = () => {
    return items.reduce((sum, item) => {
      if (item.frequency === "monthly") return sum + item.amount;
      if (item.frequency === "yearly") return sum + item.amount / 12;
      return sum;
    }, 0);
  };

  const calculateByCategory = () => {
    const byCategory = {};
    items.forEach((item) => {
      const monthlyAmount =
        item.frequency === "monthly"
          ? item.amount
          : item.frequency === "yearly"
          ? item.amount / 12
          : item.amount;
      byCategory[item.category] = (byCategory[item.category] || 0) + monthlyAmount;
    });
    return byCategory;
  };

  const categoryTotals = calculateByCategory();
  const monthlyTotal = calculateMonthlyTotal();

  const getCategoryIcon = (categoryValue) => {
    const cat = categories.find((c) => c.value === categoryValue);
    return cat?.icon || MoreHorizontal;
  };

  const getCategoryColor = (categoryValue) => {
    const cat = categories.find((c) => c.value === categoryValue);
    return cat?.color || "#718096";
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#D97757] rounded-full flex items-center justify-center">
                <Wallet className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Budget Planner</span>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-budget-btn"
                className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add Budget Item</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium text-[#1A202C] mb-2 block">Category</label>
                  <Select
                    value={newItem.category}
                    onValueChange={(v) => setNewItem({ ...newItem, category: v })}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A202C] mb-2 block">Name</label>
                  <Input
                    data-testid="item-name-input"
                    placeholder="e.g., Rent, Groceries"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#1A202C] mb-2 block">Amount</label>
                    <Input
                      data-testid="item-amount-input"
                      type="number"
                      placeholder="0.00"
                      value={newItem.amount}
                      onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#1A202C] mb-2 block">Frequency</label>
                    <Select
                      value={newItem.frequency}
                      onValueChange={(v) => setNewItem({ ...newItem, frequency: v })}
                    >
                      <SelectTrigger data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one-time">One-time</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  data-testid="save-budget-btn"
                  onClick={handleAddItem}
                  className="w-full bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
                >
                  Add Item
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card data-testid="total-monthly" className="bg-[#4A6C6F] border-0 rounded-2xl text-white col-span-1">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <PieChart className="w-6 h-6" />
                    </div>
                  </div>
                  <p className="text-white/80 text-sm mb-1">Monthly Total</p>
                  <p className="text-3xl font-bold">€{monthlyTotal.toFixed(2)}</p>
                  <p className="text-white/60 text-sm mt-2">{items.length} items tracked</p>
                </CardContent>
              </Card>

              <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl col-span-2">
                <CardHeader>
                  <CardTitle className="text-[#1A202C]">By Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {categories.map((cat) => {
                      const Icon = cat.icon;
                      const amount = categoryTotals[cat.value] || 0;
                      return (
                        <div
                          key={cat.value}
                          data-testid={`category-${cat.value}`}
                          className="flex items-center gap-3 p-3 rounded-xl bg-[#F5F5F0]"
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${cat.color}20` }}
                          >
                            <Icon className="w-4 h-4" style={{ color: cat.color }} />
                          </div>
                          <div>
                            <p className="text-xs text-[#718096]">{cat.label}</p>
                            <p className="font-medium text-[#1A202C]">€{amount.toFixed(0)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Items List */}
            <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
              <CardHeader>
                <CardTitle className="text-[#1A202C]">Budget Items</CardTitle>
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <div className="text-center py-12 text-[#718096]">
                    <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No budget items yet</p>
                    <p className="text-sm">Click "Add Expense" to start tracking</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {items.map((item) => {
                      const Icon = getCategoryIcon(item.category);
                      const color = getCategoryColor(item.category);
                      return (
                        <div
                          key={item.item_id}
                          data-testid={`budget-item-${item.item_id}`}
                          className="flex items-center justify-between p-4 rounded-xl bg-[#F5F5F0] hover:bg-[#EAEAE5] transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="w-10 h-10 rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: `${color}20` }}
                            >
                              <Icon className="w-5 h-5" style={{ color }} />
                            </div>
                            <div>
                              <p className="font-medium text-[#1A202C]">{item.name}</p>
                              <p className="text-sm text-[#718096] capitalize">
                                {item.category} • {item.frequency}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-semibold text-[#1A202C]">
                              {item.currency === "EUR" ? "€" : item.currency === "GBP" ? "£" : "$"}
                              {item.amount.toFixed(2)}
                            </span>
                            <Button
                              data-testid={`delete-${item.item_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteItem(item.item_id)}
                              className="text-[#E53E3E] hover:bg-[#E53E3E]/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default BudgetPlanner;
